import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Check, Loader2, RotateCcw, Save } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { Button } from './ui/button';
import { toast } from 'sonner';

const SaveContext = createContext(null);
export const canNavigateDashboard = () => window.dispatchEvent(new Event('tns:before-dashboard-navigation', { cancelable: true }));

const SaveSurface = ({ entries }) => {
  const { t } = useI18n();
  const reducedMotion = useReducedMotion();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const lock = useRef(false);
  const surface = useRef(null);
  const confirmation = entries.find((entry) => entry.confirmation);
  const confirming = !!confirmation;
  const active = confirmation ? [confirmation] : entries;
  const busy = saving || active.some((entry) => entry.busy);
  useEffect(() => {
    if (!active.length || !surface.current) return undefined;
    document.body.dataset.dashboardSaveOpen = 'true';
    const measure = () => document.body.style.setProperty('--tns-save-bar-height', `${surface.current?.offsetHeight + 24 || 120}px`);
    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    observer?.observe(surface.current);
    return () => { observer?.disconnect(); delete document.body.dataset.dashboardSaveOpen; document.body.style.removeProperty('--tns-save-bar-height'); };
  }, [active.length, confirming]);
  useEffect(() => {
    if ((!busy && !confirming) || !active.length) return undefined;
    const surfaces = [...document.querySelectorAll('main, [role="dialog"]')].filter((node) => !node.hasAttribute('inert'));
    surfaces.forEach((node) => node.setAttribute('inert', ''));
    return () => surfaces.forEach((node) => node.removeAttribute('inert'));
  }, [busy, confirming, active.length]);
  useEffect(() => { if (!active.length) setError(''); }, [active.length]);
  const save = async () => {
    if (lock.current || busy) return;
    lock.current = true;
    setSaving(true);
    setError('');
    try {
      for (const entry of active) {
        if (await entry.onSave?.() === false) break;
      }
    } catch {
      setError(t('تعذر حفظ التغييرات. مسودتك محفوظة هنا؛ تحقق من الاتصال وأعد المحاولة.', 'Could not save changes. Your draft is retained here; check your connection and retry.'));
    } finally { lock.current = false; setSaving(false); }
  };
  return <AnimatePresence>
    {!!active.length && <motion.div ref={surface} key="dashboard-save-bar" role="region" aria-label={t('التغييرات غير المحفوظة', 'Unsaved changes')}
      initial={{ opacity: 0, y: reducedMotion ? 0 : 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: reducedMotion ? 0 : 24 }}
      transition={{ duration: reducedMotion ? 0 : 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="pointer-events-auto mx-auto w-full max-w-5xl rounded-lg border border-primary/25 bg-background/95 p-3 shadow-[0_-8px_40px_rgba(0,0,0,0.28)] backdrop-blur-xl sm:p-4" data-testid="dashboard-save-bar">
      {confirmation?.children && <div className="mb-3 max-h-[min(35vh,260px)] overflow-y-auto text-sm">{confirmation.children}</div>}
      {error && <p role="alert" className="mb-3 text-sm text-destructive">{error}</p>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2 text-sm" role="status">
          {busy ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" /> : <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
          <span className="min-w-0 break-words">{busy ? t('جارٍ حفظ التغييرات...', 'Saving changes...') : confirmation?.title || t('لديك تغييرات غير محفوظة', 'You have unsaved changes')}</span>
        </div>
        <div className="grid w-full grid-cols-2 items-stretch gap-2 sm:flex sm:w-auto sm:shrink-0">
          <Button className="h-auto min-h-9 min-w-0 whitespace-normal px-2 py-2 sm:px-4" variant="ghost" disabled={busy} onClick={() => active.forEach((entry) => entry.onDiscard?.())}><RotateCcw className="h-4 w-4 shrink-0" /><span className="min-w-0">{t('إلغاء التغييرات', 'Discard changes')}</span></Button>
          <Button className="h-auto min-h-9 min-w-0 whitespace-normal px-2 py-2 sm:px-4" disabled={busy || active.some((entry) => entry.disabled)} onClick={save}>
            {busy ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : confirmation ? <Check className="h-4 w-4 shrink-0" /> : <Save className="h-4 w-4 shrink-0" />}
            <span className="min-w-0">{confirmation?.saveLabel || t('حفظ التغييرات', 'Save changes')}</span>
          </Button>
        </div>
      </div>
    </motion.div>}
  </AnimatePresence>;
};

export const DashboardSaveProvider = ({ children }) => {
  const { t } = useI18n();
  const [entries, setEntries] = useState({});
  const anchor = useRef(null);
  const [bounds, setBounds] = useState(null);
  useLayoutEffect(() => {
    const measure = () => {
      const rect = anchor.current?.getBoundingClientRect();
      if (rect) setBounds({ left: rect.left, width: rect.width || window.innerWidth,
        bottom: Math.max(0, window.innerHeight - rect.bottom) });
    };
    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    if (anchor.current) observer?.observe(anchor.current);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, []);
  const register = useCallback((id, value) => {
    setEntries((previous) => {
      if (!value && !previous[id]) return previous;
      const next = { ...previous };
      if (value) next[id] = value; else delete next[id];
      return next;
    });
  }, []);
  const active = Object.values(entries);
  const activeRef = useRef(active);
  activeRef.current = active;
  useEffect(() => {
    const warnNavigation = (event) => {
      if (!activeRef.current.length) return;
      event.preventDefault(); event.stopPropagation();
      toast.info(t('احفظ التغييرات أو ألغها من الشريط السفلي قبل الانتقال.', 'Save or discard changes in the bottom bar before navigating.'));
    };
    const guard = (event) => {
      if (!activeRef.current.length || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const target = event.target.closest?.('a[href], [data-dashboard-navigation]');
      if (!target || target.getAttribute('target') === '_blank') return;
      if (target.tagName === 'A') {
        const url = new URL(target.href, window.location.href);
        if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      }
      warnNavigation(event);
    };
    document.addEventListener('click', guard, true);
    window.addEventListener('tns:before-dashboard-navigation', warnNavigation);
    return () => { document.removeEventListener('click', guard, true); window.removeEventListener('tns:before-dashboard-navigation', warnNavigation); };
  }, [t]);
  useEffect(() => {
    if (!active.length) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [active.length]);
  return <SaveContext.Provider value={register}>
    {children}
    <div ref={anchor} aria-hidden="true" className="pointer-events-none absolute inset-0" />
    {bounds && createPortal(
      <div data-dashboard-save-layer="" style={bounds} className="pointer-events-none fixed z-[80] px-3 pb-[max(12px,env(safe-area-inset-bottom))] sm:px-6"><SaveSurface entries={active} /></div>,
      document.body
    )}
  </SaveContext.Provider>;
};

export const useDashboardSave = ({ dirty, busy = false, disabled = false, onSave, onDiscard, confirmation = false, title, saveLabel, children }) => {
  const register = useContext(SaveContext);
  const id = useId();
  const callbacks = useRef({ onSave, onDiscard });
  callbacks.current = { onSave, onDiscard };
  useEffect(() => {
    if (!register) return undefined;
    register(id, dirty ? { busy, disabled, confirmation, title, saveLabel,
      onSave: () => callbacks.current.onSave?.(), onDiscard: () => callbacks.current.onDiscard?.(), children } : null);
    return () => register(id, null);
  }, [register, id, dirty, busy, disabled, confirmation, title, saveLabel, children]);
  return !!register;
};

export const DashboardSaveBar = ({ dirty, busy = false, disabled = false, onSave, onDiscard, confirmation = false, title, saveLabel, children }) => {
  const registered = useDashboardSave({ dirty, busy, disabled, onSave, onDiscard, confirmation, title, saveLabel, children });
  if (registered) return null;
  return <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[80] p-3"><SaveSurface entries={dirty ? [{ busy, disabled, onSave, onDiscard, confirmation, title, saveLabel, children }] : []} /></div>;
};
